/**
 * 인테리어 OPEN 비용 정산 품의(234) — 정산 리포트 분석 결과 매핑.
 * 레거시 appr_edit_forms_open_construction_cost_settlement 의 mapAiResult·basisOf 를 그대로 옮김.
 * 진입·업로더·저장 attributes 는 233 과 같음(utils/approval/openConstructionCost).
 */

import { localize } from './localize';

type Rec = Record<string, any>;

const isBlank = (v: unknown) => v === null || v === undefined || v === '';

/** 상태값 → 뱃지 색상 클래스 — 233 과 매핑이 다름(매우우수=초록, 적합·조건부 없음) */
export const settlementStatusTagClass = (status?: string): string => {
  switch ((status || '').trim()) {
    case '적정':
    case '양호':
    case '확보':
    case '적정성 확보':
    case '우수':
    case '매우우수':
    case '달성':
      return 'is-success';
    case '보통':
    case '미확정':
      return 'is-default';
    case '주의':
    case '미흡':
    case '지연':
      return 'is-warning';
    case '높음':
    case '초과':
    case '부적정':
    case '재검토':
    case '개선필요':
    case '미달':
      return 'is-caution';
    default:
      return 'is-default';
  }
};

export type SettlementBasisMode = 'direct' | 'operating';

export type SettlementKpi = { status: string; value: string; desc: string };

export type SettlementRoi = {
  status: string;
  standardRecovery: string;
  openRecovery: string;
  settlementRecovery: string;
  recoveryVerdict: string;
  /** 이익률 + 이익금액 셀 HTML */
  standardProfitCell: string;
  openProfitCell: string;
  settlementProfitCell: string;
  profitMarginVerdict: string;
};

export type SettlementBasis = {
  opinionHTML: string;
  profitTitle: string;
  profitChip: string;
  kpiRecovery: SettlementKpi;
  kpiProfit: SettlementKpi;
  roi: SettlementRoi;
};

export type SettlementCompareRow = {
  type: string;
  benchmarkPrice: string;
  openPrice: string;
  settlementPrice: string;
  /** 차이 셀 HTML */
  vsOpenCell: string;
  vsBenchmarkCell: string;
};

export type SettlementVendorRow = {
  type: string;
  vendorName: string;
  amount: string;
  compositionPct: string;
};

export type OpenConstructionCostSettlementView = {
  project: {
    brand: string;
    projectName: string;
    channel: string;
    area: string;
    period: string;
    openDate: string;
    settlementCost: string;
    constructionType: string;
  };
  overallOpinionHTML: string;
  basis: { direct: SettlementBasis; operating: SettlementBasis };
  kpi: { cost: SettlementKpi; recovery: SettlementKpi; profit: SettlementKpi };
  costStatus: {
    status: string;
    /** 평단가 / 총액 셀 HTML */
    benchmarkCell: string;
    openCell: string;
    settlementCell: string;
    designGrade: string;
    designStatus: string;
    designNote: string;
  };
  roi: SettlementRoi;
  comparisonRows: SettlementCompareRow[];
  vendorRows: SettlementVendorRow[];
  vendorTotal: { amount: string; compositionPct: string };
  specialNotesHTML: string;
};

const comma = (n: unknown): string =>
  isBlank(n) ? '' : String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const won = (n: unknown) =>
  isBlank(n) ? '' : localize('{{amount}}원', { amount: comma(n) });
const months = (n: unknown) => (isBlank(n) ? '' : localize('{{n}}개월', { n }));
const pct = (n: unknown) => (isBlank(n) ? '' : `${n}%`);
/** 정산비교·업체별정산 표 — 값이 없거나 0이면 "-" */
const dash = (raw: unknown, formatted: string) =>
  isBlank(raw) || Number(raw) === 0 ? '-' : formatted;

const costCell = (unitPrice: string, total: string, bold: boolean) => {
  if (!unitPrice && !total) return '-';
  const totalHtml = total
    ? ` <span class="slash">/ </span><span class="tot${
        bold ? '' : ' sub'
      }">${localize('총 {{amount}}', { amount: total })}</span>`
    : '';
  return bold
    ? `<strong>${unitPrice}${totalHtml}</strong>`
    : unitPrice + totalHtml;
};

const diffCell = (settlement: unknown, other: unknown) => {
  if (
    settlement === null ||
    settlement === undefined ||
    other === null ||
    other === undefined
  )
    return '-';
  const d = Number(settlement) - Number(other);
  if (d === 0) return '-';
  const cls = d > 0 ? 'val-caution' : 'val-success';
  return `<span class="${cls}">${d > 0 ? '+' : '-'}${localize('{{amount}}원', {
    amount: comma(Math.abs(d)),
  })}</span>`;
};

const profitCell = (rate: string, amount: string, bold: boolean) => {
  if (!rate && !amount) return '-';
  const rateHtml = bold ? `<strong>${rate}</strong>` : rate;
  return rateHtml + (amount ? `<br><span class="sub">${amount}</span>` : '');
};

/** aiSummary 는 문자열(구스키마, 직접이익) 또는 {direct, operating} 객체 */
const summaryText = (src: unknown, key: SettlementBasisMode): string => {
  if (typeof src === 'string') return key === 'direct' ? src : '';
  if (!src || typeof src !== 'object') return '';
  const val = (src as Rec)[key];
  return typeof val === 'string' ? val : '';
};

const roiOf = (ro: Rec): SettlementRoi => ({
  status: ro.recoveryGrade || '',
  standardRecovery: isBlank(ro.standardRecoveryMonths)
    ? ''
    : localize('{{n}}개월 이내', { n: ro.standardRecoveryMonths }),
  openRecovery: months(ro.openRecoveryMonths),
  settlementRecovery: months(ro.settlementRecoveryMonths),
  recoveryVerdict: ro.recoveryVerdict || '',
  standardProfitCell: profitCell(
    pct(ro.standardProfitMarginPct),
    won(ro.standardProfitAmount),
    false,
  ),
  openProfitCell: profitCell(
    pct(ro.openProfitMarginPct),
    won(ro.openProfitAmount),
    false,
  ),
  settlementProfitCell: profitCell(
    pct(ro.settlementProfitMarginPct),
    won(ro.settlementProfitAmount),
    true,
  ),
  profitMarginVerdict: ro.profitMarginVerdict || '',
});

const basisOf = (
  v: Rec,
  ro: Rec,
  key: SettlementBasisMode,
): SettlementBasis => {
  const isOp = key === 'operating';
  return {
    opinionHTML: summaryText(v.aiSummary, key),
    profitTitle: localize(isOp ? '월 영업이익률' : '월 직접이익률'),
    profitChip: localize(isOp ? '영업이익률 판정' : '직접이익률 판정'),
    kpiRecovery: {
      status: ro.recoveryGrade || '',
      value: months(ro.recoveryKpiValue),
      desc: ro.recoveryKpiDesc || '',
    },
    kpiProfit: {
      status: ro.profitKpiBadge || '',
      value: pct(ro.profitKpiValue),
      desc: ro.profitKpiDesc || '',
    },
    roi: roiOf(ro),
  };
};

/** 레거시와 같이 첫 원소만 읽음(233 의 mergeAiResult 와 다름) */
export const mapSettlementResult = (
  values: unknown,
): OpenConstructionCostSettlementView | null => {
  const item = Array.isArray(values) ? values[0] : values;
  const r: Rec | null =
    item && (item as Rec).result ? (item as Rec).result : (item as Rec | null);
  if (!r || typeof r !== 'object') return null;
  const v: Rec = r.value || {};
  const cs: Rec = r.costStatus || {};
  const roiRoot: Rec = r.roiData || {};
  // 신스키마 roiData.{direct,operating} / 구스키마 평면(직접이익 단일)
  const roiDr: Rec = roiRoot.direct || roiRoot;
  const roiOp: Rec = roiRoot.operating || {};
  const compareRows: Rec[] = Array.isArray(r.settlementComparisonData)
    ? r.settlementComparisonData
    : [];
  const vendorRows: Rec[] = Array.isArray(r.vendorSettlementData)
    ? r.vendorSettlementData
    : [];
  const vendorTotal: Rec = r.vendorSettlementTotal || {};

  return {
    project: {
      brand: v.brand || '',
      projectName: v.title || '',
      channel: v.channel || '',
      area:
        v.areaInPY !== null && v.areaInPY !== undefined
          ? `${v.areaInPY} PY`
          : '',
      period:
        v.startDate && v.endDate
          ? `${v.startDate} ~ ${v.endDate}`
          : v.startDate || v.endDate || '',
      openDate: v.openDate || '',
      settlementCost: won(v.settlementTotalCost),
      constructionType: v.constructionType || '',
    },
    overallOpinionHTML:
      summaryText(v.aiSummary, 'direct') ||
      summaryText(v.aiSummary, 'operating'),
    basis: {
      direct: basisOf(v, roiDr, 'direct'),
      operating: basisOf(v, roiOp, 'operating'),
    },
    kpi: {
      cost: {
        status: cs.kpiVerdict || '',
        value: cs.kpiValue || '',
        desc: cs.kpiDesc || '',
      },
      recovery: {
        status: roiDr.recoveryGrade || '',
        value: months(roiDr.recoveryKpiValue),
        desc: roiDr.recoveryKpiDesc || '',
      },
      profit: {
        status: roiDr.profitKpiBadge || '',
        value: pct(roiDr.profitKpiValue),
        desc: roiDr.profitKpiDesc || '',
      },
    },
    costStatus: {
      status: cs.kpiVerdict || '',
      benchmarkCell: costCell(
        won(cs.benchmarkUnitCostPerPY),
        won(cs.benchmarkTotalCost),
        false,
      ),
      openCell: costCell(
        won(cs.openUnitCostPerPY),
        won(cs.openTotalCost),
        false,
      ),
      settlementCell: costCell(
        won(cs.settlementUnitCostPerPY),
        won(cs.settlementTotalCost),
        true,
      ),
      designGrade: cs.designGrade || '',
      designStatus: cs.designGradeVerdict || '',
      designNote: cs.designGradeNote || '',
    },
    roi: roiOf(roiDr),
    comparisonRows: compareRows.map((c) => ({
      type: c.type || '',
      benchmarkPrice: dash(
        c.benchmarkUnitCostPerPY,
        won(c.benchmarkUnitCostPerPY),
      ),
      openPrice: dash(c.openUnitCostPerPY, won(c.openUnitCostPerPY)),
      settlementPrice: dash(
        c.settlementUnitCostPerPY,
        won(c.settlementUnitCostPerPY),
      ),
      vsOpenCell: diffCell(c.settlementUnitCostPerPY, c.openUnitCostPerPY),
      vsBenchmarkCell: diffCell(
        c.settlementUnitCostPerPY,
        c.benchmarkUnitCostPerPY,
      ),
    })),
    vendorRows: vendorRows.map((c) => ({
      type: c.type || '',
      vendorName: c.vendorName || '',
      amount: dash(c.amount, won(c.amount)),
      compositionPct: dash(
        c.compositionPct,
        `${Number(c.compositionPct).toFixed(1)}%`,
      ),
    })),
    vendorTotal: {
      amount: dash(vendorTotal.amount, won(vendorTotal.amount)),
      compositionPct: dash(
        vendorTotal.compositionPct,
        `${Number(vendorTotal.compositionPct).toFixed(1)}%`,
      ),
    },
    specialNotesHTML: v.aiAnomalyAnalysis || '',
  };
};

export const hasSettlementBasis = (
  b?: SettlementBasis | null,
): b is SettlementBasis =>
  !!(b && (b.opinionHTML || b.roi.settlementRecovery || b.kpiRecovery.value));

/** 작성 화면 기본 기준 — 직접이익 우선(레거시 2026-08-25 요청) */
export const initialSettlementBasisMode = (
  view: OpenConstructionCostSettlementView,
): SettlementBasisMode | null => {
  if (hasSettlementBasis(view.basis.direct)) return 'direct';
  if (hasSettlementBasis(view.basis.operating)) return 'operating';
  return null;
};

export const canToggleSettlementBasis = (
  view: OpenConstructionCostSettlementView,
) =>
  hasSettlementBasis(view.basis.direct) &&
  hasSettlementBasis(view.basis.operating);
